// An organization's security settings (admins): how members may sign in, two-factor for everyone, how long a sign-in
// lasts, and company email domains people can join with; plus requests to join, and the screen shown to someone whose
// sign-in doesn't meet the organization's rules.
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../../lib/api";
import { Alert, Avatar, Badge, Button, Card, Field, Logo, Spinner, cx } from "../../components/ui";
import { useProviders } from "../auth";
import { TwoFactorCard } from "../profile";
import { useOrg } from ".";

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Please try again.");
type Method = "password" | "email_link" | "google" | "microsoft";
const METHOD_LABEL: Record<Method, string> = { password: "Password", email_link: "Emailed sign-in link", google: "Google", microsoft: "Microsoft" };
const HOURS: { value: number | null; label: string }[] = [
  { value: null, label: "Stay signed in (up to 30 days)" }, { value: 8, label: "8 hours" }, { value: 24, label: "1 day" },
  { value: 72, label: "3 days" }, { value: 168, label: "1 week" },
];

// ---------------------------------------------------------------- security settings

export function SecurityCard() {
  const { org } = useOrg();
  const qc = useQueryClient();
  const providers = useProviders();
  const available: Method[] = ["password", "email_link", ...providers.map((p) => p.id as Method)];
  const s = org.settings;
  const [any, setAny] = useState(!s.signInMethods?.length);
  const [methods, setMethods] = useState<Method[]>((s.signInMethods as Method[] | undefined) ?? available);
  const [requireMfa, setRequireMfa] = useState(Boolean(s.requireMfa));
  const [hours, setHours] = useState<number | null>(s.sessionMaxHours ?? null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      await api(`/orgs/${org.slug}`, { method: "PATCH", body: { settings: { signInMethods: any ? null : methods, requireMfa, sessionMaxHours: hours } } });
      await qc.invalidateQueries({ queryKey: ["org", org.slug] });
      setMsg({ kind: "success", text: "Security settings saved. They apply to everyone straight away." });
    } catch (err) { setMsg({ kind: "error", text: errorText(err) }); } finally { setBusy(false); }
  }
  const toggle = (m: Method) => setMethods((list) => (list.includes(m) ? list.filter((x) => x !== m) : [...list, m]));
  return (
    <Card>
      <h2 className="font-semibold">Security</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Rules for everyone's sign-in to {org.name}, owners included. Someone who doesn't meet them is asked to fix that before they continue.</p>
      {msg && <div className="mt-4"><Alert kind={msg.kind}>{msg.text}</Alert></div>}
      <form onSubmit={save} className="mt-4 space-y-5">
        <fieldset>
          <legend className="text-sm font-medium">Ways to sign in</legend>
          <label className="mt-2 flex items-center gap-2 text-sm"><input type="radio" name="any" checked={any} onChange={() => setAny(true)} className="h-4 w-4" />Any way {`(${available.map((m) => METHOD_LABEL[m].toLowerCase()).join(", ")})`}</label>
          <label className="mt-1 flex items-center gap-2 text-sm"><input type="radio" name="any" checked={!any} onChange={() => setAny(false)} className="h-4 w-4" />Only these:</label>
          <div className={cx("ml-6 mt-1 flex flex-wrap gap-x-5 gap-y-1", any && "opacity-50")}>
            {available.map((m) => (
              <label key={m} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={methods.includes(m)} onChange={() => toggle(m)} disabled={any} className="h-4 w-4 rounded" />{METHOD_LABEL[m]}</label>
            ))}
          </div>
        </fieldset>
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" checked={requireMfa} onChange={(e) => setRequireMfa(e.target.checked)} className="mt-0.5 h-4 w-4 rounded" />
          <span><span className="font-medium">Require two-factor sign-in</span><br /><span className="text-slate-500">Everyone needs an authenticator app code as well. Turn it on for yourself first (Your account).</span></span>
        </label>
        <label className="block text-sm font-medium">Sign in again after
          <select value={hours ?? ""} onChange={(e) => setHours(e.target.value ? Number(e.target.value) : null)} className="mt-1.5 block w-full max-w-xs rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal dark:border-slate-700 dark:bg-slate-900">
            {HOURS.map((h) => <option key={h.label} value={h.value ?? ""}>{h.label}</option>)}
          </select>
        </label>
        <div className="flex justify-end"><Button type="submit" loading={busy}>Save security settings</Button></div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------- company email domains

type Domain = { id: string; domain: string; verified: boolean; join: "off" | "request" | "auto"; record: { type: string; host: string; value: string } | null };
const JOIN_LABEL = { request: "Can ask to join (an admin approves)", auto: "Join straight in", off: "Can't join on their own" };

export function DomainsCard() {
  const { org } = useOrg();
  const qc = useQueryClient();
  const key = ["domains", org.slug];
  const data = useQuery({ queryKey: key, queryFn: () => api<{ domains: Domain[] }>(`/orgs/${org.slug}/domains`) });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function run(id: string, fn: () => Promise<{ domains: Domain[] }>) {
    setBusy(id); setError(null);
    try { qc.setQueryData(key, await fn()); return true; } catch (err) { setError(errorText(err)); return false; } finally { setBusy(null); }
  }
  async function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const domain = String(new FormData(form).get("domain"));
    if (await run("add", () => api(`/orgs/${org.slug}/domains`, { method: "POST", body: { domain } }))) form.reset();
  }
  return (
    <Card>
      <h2 className="font-semibold">Company email domains</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">People with a confirmed email address at your domain can find {org.name} and join, without an invitation. Prove you own the domain with a DNS record first.</p>
      {error && <div className="mt-4"><Alert>{error}</Alert></div>}
      {data.isLoading ? <div className="flex justify-center p-6 text-brand-600"><Spinner /></div> : (
        <ul className="mt-4 space-y-3">
          {data.data?.domains.map((d) => (
            <li key={d.id} className="rounded-xl p-4 ring-1 ring-slate-200 dark:ring-slate-800">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">@{d.domain}</span>
                {d.verified ? <Badge tone="brand">Verified</Badge> : <Badge tone="amber">Not verified yet</Badge>}
                <button type="button" onClick={() => { if (confirm(`Remove ${d.domain}?`)) void run(d.id, () => api(`/orgs/${org.slug}/domains/${d.id}`, { method: "DELETE" })); }} className="ml-auto text-sm text-red-600 hover:underline">Remove</button>
              </div>
              {d.record ? (
                <div className="mt-3 space-y-2 text-sm">
                  <p>At your domain's DNS provider, add this <strong>TXT</strong> record, then press Verify:</p>
                  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg bg-slate-100 p-3 font-mono text-xs dark:bg-slate-800">
                    <dt className="text-slate-500">Host</dt><dd className="select-all break-all">{d.record.host} (or @)</dd>
                    <dt className="text-slate-500">Value</dt><dd className="select-all break-all" aria-label={`TXT value for ${d.domain}`}>{d.record.value}</dd>
                  </dl>
                  <Button size="sm" onClick={() => void run(d.id, () => api(`/orgs/${org.slug}/domains/${d.id}/verify`, { method: "POST" }))} loading={busy === d.id}>Verify</Button>
                </div>
              ) : (
                <label className="mt-3 flex flex-wrap items-center gap-2 text-sm">People @{d.domain}:
                  <select aria-label={`Who can join with @${d.domain}`} value={d.join} disabled={busy !== null}
                    onChange={(e) => void run(d.id, () => api(`/orgs/${org.slug}/domains/${d.id}`, { method: "PATCH", body: { join: e.target.value } }))}
                    className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900">
                    {(Object.keys(JOIN_LABEL) as Domain["join"][]).map((j) => <option key={j} value={j}>{JOIN_LABEL[j]}</option>)}
                  </select>
                </label>
              )}
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="mt-4 flex flex-wrap items-end gap-2">
        <Field label="Add a domain" name="domain" placeholder="acme.com" required className="min-w-56 flex-1" />
        <Button type="submit" variant="secondary" loading={busy === "add"}>Add</Button>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------- requests to join (members page)

type JoinRequest = { id: string; userId: string; name: string; email: string; createdAt: string };

export function JoinRequests() {
  const { org } = useOrg();
  const qc = useQueryClient();
  const key = ["join-requests", org.slug];
  const data = useQuery({ queryKey: key, queryFn: () => api<{ requests: JoinRequest[] }>(`/orgs/${org.slug}/join-requests`) });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!data.data?.requests.length) return null;
  async function decide(id: string, approve: boolean) {
    setBusy(id); setError(null);
    try {
      qc.setQueryData(key, await api(`/orgs/${org.slug}/join-requests/${id}`, { method: "POST", body: { approve } }));
      await qc.invalidateQueries({ queryKey: ["members", org.slug] });
    } catch (err) { setError(errorText(err)); } finally { setBusy(null); }
  }
  return (
    <>
      <h2 className="mb-3 mt-8 text-lg font-semibold">Asking to join</h2>
      {error && <div className="mb-3"><Alert>{error}</Alert></div>}
      <Card className="p-0">
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {data.data.requests.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <Avatar name={r.name} />
              <div className="min-w-0 flex-1"><div className="truncate font-medium">{r.name}</div><div className="truncate text-sm text-slate-500">{r.email} · asked {new Date(r.createdAt).toLocaleDateString()}</div></div>
              <Button size="sm" onClick={() => decide(r.id, true)} loading={busy === r.id}>Let in</Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => decide(r.id, false)} disabled={busy === r.id}>Decline</Button>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------- not meeting the rules

export const POLICY_CODES = ["ORG_MFA_REQUIRED", "ORG_SIGN_IN_METHOD", "ORG_SESSION_TOO_OLD"];

export function PolicyBlock({ slug, error, onRetry }: { slug: string; error: ApiError; onRetry: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  async function signInAgain() {
    await api("/auth/logout", { method: "POST" }).catch(() => undefined);
    qc.clear();
    navigate(`/login?next=${encodeURIComponent(`/o/${slug}`)}`, { replace: true });
  }
  return (
    <div className="mx-auto max-w-2xl space-y-4 p-6 pt-16">
      <Logo />
      <Card>
        <h1 className="text-lg font-semibold">{error.code === "ORG_MFA_REQUIRED" ? "Two-factor sign-in needed" : "Please sign in again"}</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{error.message}</p>
        {error.code !== "ORG_MFA_REQUIRED" && <Button className="mt-4" onClick={signInAgain}>Sign in again</Button>}
      </Card>
      {error.code === "ORG_MFA_REQUIRED" && <><TwoFactorCard /><Button variant="secondary" onClick={onRetry}>Continue</Button></>}
    </div>
  );
}

// ---------------------------------------------------------------- meetings and recording

const KEEP: { value: number | null; label: string }[] = [
  { value: null, label: "Keep until someone deletes them" }, { value: 7, label: "7 days" }, { value: 30, label: "30 days" },
  { value: 90, label: "90 days" }, { value: 180, label: "180 days" }, { value: 365, label: "1 year" },
];
const LENGTH: { value: number | null; label: string }[] = [
  { value: null, label: "No limit (beyond the plan's)" }, { value: 30, label: "30 minutes" }, { value: 60, label: "1 hour" },
  { value: 90, label: "1½ hours" }, { value: 120, label: "2 hours" }, { value: 240, label: "4 hours" }, { value: 480, label: "8 hours" },
];

export function MeetingRulesCard() {
  const { org } = useOrg();
  const qc = useQueryClient();
  const s = org.settings;
  const [recording, setRecording] = useState(s.recordingAllowed ?? "everyone");
  const [keep, setKeep] = useState<number | null>(s.recordingRetentionDays ?? null);
  const [guests, setGuests] = useState(s.guestsAllowed !== false);
  const [everyoneWaits, setEveryoneWaits] = useState(Boolean(s.lobbyForEveryone));
  const [length, setLength] = useState<number | null>(s.maxMeetingMinutes ?? null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  async function save(e: FormEvent) {
    e.preventDefault();
    if (keep !== (s.recordingRetentionDays ?? null) && keep !== null && !confirm(`Recordings older than ${KEEP.find((k) => k.value === keep)!.label} will be deleted for good, including ones made before. Continue?`)) return;
    setBusy(true); setMsg(null);
    try {
      await api(`/orgs/${org.slug}`, { method: "PATCH", body: { settings: { recordingAllowed: recording, recordingRetentionDays: keep, guestsAllowed: guests, lobbyForEveryone: everyoneWaits, maxMeetingMinutes: length } } });
      await qc.invalidateQueries({ queryKey: ["org", org.slug] });
      setMsg({ kind: "success", text: "Meeting and recording settings saved." });
    } catch (err) { setMsg({ kind: "error", text: errorText(err) }); } finally { setBusy(false); }
  }
  const select = "mt-1.5 block w-full max-w-xs rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal dark:border-slate-700 dark:bg-slate-900";
  return (
    <Card>
      <h2 className="font-semibold">Meetings and recording</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Rules for every call and meeting in {org.name}.</p>
      {msg && <div className="mt-4"><Alert kind={msg.kind}>{msg.text}</Alert></div>}
      <form onSubmit={save} className="mt-4 space-y-5">
        <label className="block text-sm font-medium">Who can record
          <select value={recording} onChange={(e) => setRecording(e.target.value as typeof recording)} className={select}>
            <option value="everyone">Anyone in the call (meetings: the host)</option>
            <option value="admins">Only admins and owners</option>
            <option value="nobody">Nobody (recording off)</option>
          </select>
        </label>
        <label className="block text-sm font-medium">Keep recordings for
          <select value={keep ?? ""} onChange={(e) => setKeep(e.target.value ? Number(e.target.value) : null)} className={select}>
            {KEEP.map((k) => <option key={k.label} value={k.value ?? ""}>{k.label}</option>)}
          </select>
          <span className="mt-1 block text-xs font-normal text-slate-500">Older recordings are deleted automatically, files and all.</span>
        </label>
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" checked={guests} onChange={(e) => setGuests(e.target.checked)} className="mt-0.5 h-4 w-4 rounded" />
          <span><span className="font-medium">Guests can join meetings</span><br /><span className="text-slate-500">People from outside {org.name} ask to join and wait to be let in. Off: meetings are for members only.</span></span>
        </label>
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" checked={everyoneWaits} onChange={(e) => setEveryoneWaits(e.target.checked)} className="mt-0.5 h-4 w-4 rounded" />
          <span><span className="font-medium">Everyone waits in the lobby</span><br /><span className="text-slate-500">Colleagues too, not only guests: the host lets each person in.</span></span>
        </label>
        <label className="block text-sm font-medium">Longest a meeting or group call can run
          <select value={length ?? ""} onChange={(e) => setLength(e.target.value ? Number(e.target.value) : null)} className={select}>
            {LENGTH.map((l) => <option key={l.label} value={l.value ?? ""}>{l.label}</option>)}
          </select>
          <span className="mt-1 block text-xs font-normal text-slate-500">Everyone is warned 5 minutes before. One-to-one calls are never cut off.</span>
        </label>
        <div className="flex justify-end"><Button type="submit" loading={busy}>Save meeting settings</Button></div>
      </form>
    </Card>
  );
}
